"use client";

import React, { useState, useMemo } from "react";
import { Grid2x2, Scale, CircleHelp, ArrowRight, RotateCcw, Info, CircleCheck, FileText, Download, SlidersHorizontal } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import DataTable, { DataTableColumn } from "@/components/common/DataTable";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
interface StandardSpec {
  mesh: number;
  wireMm: number;
  wireInch: number;
  apertureMm: number;
  micron: number;
  openArea: number;
  weightKgM2: number;
  standard: string;
}
const STANDARD_SPECS: StandardSpec[] = [{
  mesh: 10,
  wireMm: 0.8,
  wireInch: 0.0315,
  apertureMm: 1.74,
  micron: 1740,
  openArea: 46.9,
  weightKgM2: 1.62,
  standard: "ASTM E2016"
}, {
  mesh: 20,
  wireMm: 0.4,
  wireInch: 0.0157,
  apertureMm: 0.87,
  micron: 870,
  openArea: 46.9,
  weightKgM2: 0.81,
  standard: "ISO 9044"
}, {
  mesh: 40,
  wireMm: 0.25,
  wireInch: 0.0098,
  apertureMm: 0.385,
  micron: 385,
  openArea: 36.8,
  weightKgM2: 0.63,
  standard: "ISO 9044"
}, {
  mesh: 60,
  wireMm: 0.19,
  wireInch: 0.0075,
  apertureMm: 0.233,
  micron: 233,
  openArea: 30.4,
  weightKgM2: 0.55,
  standard: "ASTM E2016"
}, {
  mesh: 80,
  wireMm: 0.12,
  wireInch: 0.0047,
  apertureMm: 0.198,
  micron: 198,
  openArea: 38.8,
  weightKgM2: 0.29,
  standard: "ISO 9044"
}, {
  mesh: 100,
  wireMm: 0.1,
  wireInch: 0.0039,
  apertureMm: 0.154,
  micron: 154,
  openArea: 36.8,
  weightKgM2: 0.25,
  standard: "ISO 9044"
}, {
  mesh: 150,
  wireMm: 0.063,
  wireInch: 0.0025,
  apertureMm: 0.106,
  micron: 106,
  openArea: 39.3,
  weightKgM2: 0.15,
  standard: "ASTM E2016"
}, {
  mesh: 200,
  wireMm: 0.05,
  wireInch: 0.002,
  apertureMm: 0.077,
  micron: 77,
  openArea: 36.8,
  weightKgM2: 0.13,
  standard: "ISO 9044"
}, {
  mesh: 325,
  wireMm: 0.035,
  wireInch: 0.0014,
  apertureMm: 0.043,
  micron: 43,
  openArea: 30.4,
  weightKgM2: 0.1,
  standard: "ASTM E2016"
}, {
  mesh: 400,
  wireMm: 0.03,
  wireInch: 0.0012,
  apertureMm: 0.034,
  micron: 34,
  openArea: 28.2,
  weightKgM2: 0.09,
  standard: "ISO 9044"
}];
const DENSITY_MAP: Record<string, {
  density: number;
  name: string;
}> = {
  "304": {
    density: 7930,
    name: "AISI 304 (1.4301)"
  },
  "316": {
    density: 7980,
    name: "AISI 316 (1.4401)"
  },
  "316L": {
    density: 7980,
    name: "AISI 316L (1.4404)"
  },
  "310S": {
    density: 7980,
    name: "AISI 310S (1.4845)"
  },
  "904L": {
    density: 8000,
    name: "AISI 904L (1.4539)"
  },
  "inconel600": {
    density: 8470,
    name: "Inconel 600 (2.4816)"
  }
};
export default function WovenCalculations() {
  const {
    lang
  } = useLanguage();

  // Calculator 1: Aperture & Open Area
  const [apMesh, setApMesh] = useState<number>(100);
  const [apWireMm, setApWireMm] = useState<number>(0.1);
  const [weaveType, setWeaveType] = useState<string>("plain");

  // Calculator 2: Mesh & Roll Weight
  const [wtMesh, setWtMesh] = useState<number>(100);
  const [wtWireMm, setWtWireMm] = useState<number>(0.1);
  const [alloy, setAlloy] = useState<string>("316L");
  const [rollWidth, setRollWidth] = useState<number>(1.0);
  const [rollLength, setRollLength] = useState<number>(30.0);

  // Table search/filter
  const [tableSearch, setTableSearch] = useState<string>("");

  // Aperture Calculation Logic
  const pitchMm = apMesh > 0 ? 25.4 / apMesh : 0;
  const rawApertureMm = pitchMm - apWireMm;
  const apertureMm = rawApertureMm > 0 ? rawApertureMm : 0;
  const apertureMicron = apertureMm * 1000;
  const openAreaPercent = pitchMm > 0 && apertureMm > 0 ? Math.pow(apertureMm / pitchMm, 2) * 100 : 0;

  // Weight Calculation Logic
  const wireRadiusM = wtWireMm / 2 * 1e-3;
  const wireSectionAreaM2 = Math.PI * Math.pow(wireRadiusM, 2);
  const wireMetersPerM2 = 2 * wtMesh * 39.3701 * 1.02; // 1.02 crimp factor allowance
  const steelDensity = DENSITY_MAP[alloy]?.density || 7980;
  const weightKgPerM2 = wireSectionAreaM2 * wireMetersPerM2 * steelDensity;
  const totalAreaM2 = rollWidth * rollLength;
  const totalRollWeightKg = weightKgPerM2 * totalAreaM2;
  const totalRollWeightLbs = totalRollWeightKg * 2.20462;
  const resetApertureCalc = () => {
    setApMesh(100);
    setApWireMm(0.1);
    setWeaveType("plain");
  };
  const resetWeightCalc = () => {
    setWtMesh(100);
    setWtWireMm(0.1);
    setAlloy("316L");
    setRollWidth(1.0);
    setRollLength(30.0);
  };
  const filteredSpecs = useMemo(() => {
    return STANDARD_SPECS.filter(spec => {
      if (!tableSearch) return true;
      const query = tableSearch.toLowerCase();
      return spec.mesh.toString().includes(query) || spec.micron.toString().includes(query) || spec.standard.toLowerCase().includes(query);
    });
  }, [tableSearch]);
  const copy = {
    badge: pick(lang, {
      en: "Engineering Utilities",
      zh: "工程计算实用工具",
      es: "Utilidades de ingeniería",
      de: "Ingenieur-Dienstprogramme",
      fr: "Utilitaires d'ingénierie",
      pt: "Utilitários de engenharia"
    }),
    h2: pick(lang, {
      en: "Woven Wire Mesh Calculations",
      zh: "编织金属丝网工程计算",
      es: "Cálculos de malla metálica tejida",
      de: "Berechnungen für Drahtgewebe",
      fr: "Calculs de toiles métalliques tissées",
      pt: "Cálculos de telas metálicas tecidas"
    }),
    lead: pick(lang, {
      en: "Verify key mesh metrics including pitch, aperture size, open area percentage, micron rating, and estimated shipment weight according to ASTM E2016 and ISO 9044 B2B manufacturing standards.",
      zh: "依据 ASTM E2016 与 ISO 9044 工业制造标准，校核编织网孔距、开孔尺寸、开孔率百分比、微米精度及发运预估重量等关键指标。",
      es: "Verifique métricas clave de la malla incluyendo paso, tamaño de abertura, porcentaje de área abierta, micras y peso estimado según normas ASTM E2016 e ISO 9044.",
      de: "Überprüfen Sie wichtige Gewebekenngrößen wie Teilung, Maschenweite, freie Siebfläche, Mikronwert und geschätztes Versandgewicht nach ASTM E2016 und ISO 9044.",
      fr: "Vérifiez les paramètres clés de la toile, notamment le pas, l'ouverture de maille, le pourcentage de vide, le seuil en microns et le poids d'expédition selon ASTM E2016 et ISO 9044.",
      pt: "Verifique parâmetros essenciais da malha incluindo passo, tamanho da abertura, percentual de área aberta, mícrons e peso estimado conforme normas ASTM E2016 e ISO 9044."
    }),
    tabAperture: pick(lang, {
      en: "Aperture & Open Area",
      zh: "孔径与开孔率",
      es: "Abertura y área abierta",
      de: "Maschenweite & freie Siebfläche",
      fr: "Ouverture et surface ouverte",
      pt: "Abertura e área aberta"
    }),
    tabWeight: pick(lang, {
      en: "Weight & Roll Estimator",
      zh: "重量与整卷估算",
      es: "Estimador de peso y rollo",
      de: "Gewichts- & Rollenrechner",
      fr: "Estimateur de poids et rouleau",
      pt: "Estimador de peso e rolo"
    }),
    tabReference: pick(lang, {
      en: "Standard Spec Chart",
      zh: "标准规格对照表",
      es: "Tabla de especificaciones estándar",
      de: "Standard-Spezifikationstabelle",
      fr: "Tableau des spécifications standard",
      pt: "Tabela de especificações padrão"
    }),
    meshInputsTitle: pick(lang, {
      en: "Mesh Inputs",
      zh: "丝网参数输入",
      es: "Parámetros de malla",
      de: "Gewebeeingaben",
      fr: "Paramètres de la toile",
      pt: "Parâmetros da malha"
    }),
    resetBtn: pick(lang, {
      en: "Reset",
      zh: "重置",
      es: "Restablecer",
      de: "Zurücksetzen",
      fr: "Réinitialiser",
      pt: "Redefinir"
    }),
    meshInputsDesc: pick(lang, {
      en: "Enter mesh count per linear inch and wire diameter to compute open screen parameters.",
      zh: "输入每线性英寸目数与丝径，计算开孔率与几何参数。",
      es: "Ingrese el número de mallas por pulgada lineal y el diámetro del alambre para calcular los parámetros.",
      de: "Geben Sie die Maschenzahl pro linearem Zoll und den Drahtdurchmesser ein, um die Parameter zu berechnen.",
      fr: "Saisissez le nombre de mailles par pouce linéaire et le diamètre du fil pour calculer les paramètres.",
      pt: "Insira a contagem de malha por polegada linear e o diâmetro do arame para calcular os parâmetros."
    }),
    labelMeshCount: pick(lang, {
      en: "Mesh Count (N)",
      zh: "目数 (N)",
      es: "Número de mallas (N)",
      de: "Maschenzahl (N)",
      fr: "Nombre de mailles (N)",
      pt: "Contagem de malha (N)"
    }),
    warpWeftInch: pick(lang, {
      en: "Warp & Weft / inch",
      zh: "经纬向 / 英寸",
      es: "Urdimbre y trama / pulg.",
      de: "Kette & Schuss / Zoll",
      fr: "Chaîne et trame / pouce",
      pt: "Urdume e trama / pol."
    }),
    meshTooltip: pick(lang, {
      en: "Number of openings per linear inch (25.4 mm) along the weave direction.",
      zh: "沿编织方向每线性英寸 (25.4 mm) 内的网孔数量。",
      es: "Número de aberturas por pulgada lineal (25.4 mm) a lo largo de la dirección de tejido.",
      de: "Anzahl der Öffnungen pro linearem Zoll (25,4 mm) entlang der Webregion.",
      fr: "Nombre d'ouvertures par pouce linéaire (25,4 mm) le long du sens de tissage.",
      pt: "Número de aberturas por polegada linear (25,4 mm) ao longo da direção de tecelagem."
    }),
    labelWireDia: pick(lang, {
      en: "Wire Diameter (d)",
      zh: "丝径 (d)",
      es: "Diámetro del alambre (d)",
      de: "Drahtdurchmesser (d)",
      fr: "Diamètre du fil (d)",
      pt: "Diâmetro do arame (d)"
    }),
    weaveStyleLabel: pick(lang, {
      en: "Weave Style",
      zh: "编织形式",
      es: "Tipo de tejido",
      de: "Webart",
      fr: "Type de tissage",
      pt: "Tipo de trama"
    }),
    stylePlain: pick(lang, {
      en: "Plain Weave (1 over / 1 under)",
      zh: "平纹编织 (一上一下)",
      es: "Tejido liso (1 arriba / 1 abajo)",
      de: "Leinwandbindung (1 über / 1 unter)",
      fr: "Tissage uni (1 dessus / 1 dessous)",
      pt: "Trama lisa (1 por cima / 1 por baixo)"
    }),
    styleTwilled: pick(lang, {
      en: "Twilled Weave (2 over / 2 under)",
      zh: "斜纹编织 (二上二下)",
      es: "Tejido sarga (2 arriba / 2 abajo)",
      de: "Köperbindung (2 über / 2 unter)",
      fr: "Tissage sergé (2 dessus / 2 dessous)",
      pt: "Trama sarja (2 por cima / 2 por baixo)"
    }),
    styleDutch: pick(lang, {
      en: "Dutch Weave (High Density Filter Mesh)",
      zh: "荷兰编织 (高密过滤网)",
      es: "Tejido Dutch (malla filtrante de alta densidad)",
      de: "Tressengewebe (Filtergewebe hoher Dichte)",
      fr: "Tissage hollandais (toile filtrante haute densité)",
      pt: "Trama holandesa (malha filtrante de alta densidade)"
    }),
    keyFormulasTitle: pick(lang, {
      en: "Key Formulas:",
      zh: "关键公式：",
      es: "Fórmulas clave:",
      de: "Wichtige Formeln:",
      fr: "Formules clés :",
      pt: "Fórmulas essenciais:"
    }),
    calcSpecTitle: pick(lang, {
      en: "Calculated Specification",
      zh: "计算结果规格",
      es: "Especificación calculada",
      de: "Berechnete Spezifikation",
      fr: "Spécification calculée",
      pt: "Especificação calculada"
    }),
    validGeometry: pick(lang, {
      en: "Valid Geometry",
      zh: "几何参数有效",
      es: "Geometría válida",
      de: "Gültige Geometrie",
      fr: "Géométrie valide",
      pt: "Geometria válida"
    }),
    invalidWire: pick(lang, {
      en: "Invalid (Wire Too Thick)",
      zh: "几何冲突 (丝径过大)",
      es: "No válido (alambre demasiado grueso)",
      de: "Ungültig (Draht zu dick)",
      fr: "Invalide (fil trop épais)",
      pt: "Inválido (arame muito espesso)"
    }),
    calcSpecDesc: pick(lang, {
      en: "Real-time mesh geometry derived from input values.",
      zh: "基于输入数值实时得出的丝网几何数据。",
      es: "Geometría de malla en tiempo real calculada a partir de los valores ingresados.",
      de: "Echtzeit-Gewebegeometrie basierend auf den eingegebenen Werten.",
      fr: "Géométrie de toile en temps réel calculée à partir des entrées.",
      pt: "Geometria da malha em tempo real obtida dos valores fornecidos."
    }),
    interferenceWarning: (wire: number, pitch: string, mesh: number) => pick(lang, {
      en: `Interference Warning: The selected wire diameter (${wire} mm) exceeds the pitch interval (${pitch} mm) for ${mesh} mesh. Please decrease wire diameter or lower mesh count.`,
      zh: `干涉预警：所选丝径 (${wire} mm) 超出 ${mesh} 目时的孔距间隙 (${pitch} mm)。请调小丝径或降低目数。`,
      es: `Aviso de interferencia: El diámetro del alambre (${wire} mm) supera el paso (${pitch} mm) para ${mesh} mesh. Reduzca el diámetro o el número de mallas.`,
      de: `Geometrie-Warnung: Der Drahtdurchmesser (${wire} mm) überschreitet die Teilung (${pitch} mm) bei ${mesh} Mesh. Bitte Drahtdurchmesser verringern oder Maschenzahl anpassen.`,
      fr: `Avertissement d'interférence : Le diamètre du fil (${wire} mm) dépasse le pas (${pitch} mm) pour ${mesh} mesh. Veuillez réduire le diamètre ou le nombre de mailles.`,
      pt: `Aviso de interferência: O diâmetro do arame (${wire} mm) excede o passo (${pitch} mm) para ${mesh} mesh. Reduza o diâmetro ou a contagem de malha.`
    }),
    apertureSizeLabel: pick(lang, {
      en: "Aperture Size (w)",
      zh: "孔径大小 (w)",
      es: "Tamaño de abertura (w)",
      de: "Maschenweite (w)",
      fr: "Ouverture de maille (w)",
      pt: "Tamanho da abertura (w)"
    }),
    openAreaLabel: pick(lang, {
      en: "Open Area (Ao)",
      zh: "开孔率 (Ao)",
      es: "Área abierta (Ao)",
      de: "Freie Siebfläche (Ao)",
      fr: "Surface ouverte (Ao)",
      pt: "Área aberta (Ao)"
    }),
    passageRatio: pick(lang, {
      en: "Passage ratio",
      zh: "流通面积占比",
      es: "Proporción de paso",
      de: "Durchgangsanteil",
      fr: "Ratio de passage",
      pt: "Proporção de passagem"
    }),
    pitchLabel: pick(lang, {
      en: "Weave Pitch (p)",
      zh: "孔距中心距 (p)",
      es: "Paso de tejido (p)",
      de: "Teilung (p)",
      fr: "Pas de tissage (p)",
      pt: "Passo da trama (p)"
    }),
    centerToCenter: pick(lang, {
      en: "Center-to-center",
      zh: "中心到中心",
      es: "Centro a centro",
      de: "Mitte zu Mitte",
      fr: "Centre à centre",
      pt: "Centro a centro"
    }),
    ratioLabel: pick(lang, {
      en: "Aperture / Wire Ratio",
      zh: "孔径 / 丝径比",
      es: "Relación abertura / alambre",
      de: "Verhältnis Masche / Draht",
      fr: "Ratio ouverture / fil",
      pt: "Razão abertura / arame"
    }),
    structuralProp: pick(lang, {
      en: "Structural proportion",
      zh: "结构几何比",
      es: "Proporción estructural",
      de: "Struktureller Anteil",
      fr: "Proportion structurelle",
      pt: "Proporção estrutural"
    }),
    scaleModelTitle: pick(lang, {
      en: "Geometric Scale Model",
      zh: "几何比例示意",
      es: "Modelo a escala geométrica",
      de: "Geometrisches Maßstabsmodell",
      fr: "Modèle à l'échelle géométrique",
      pt: "Modelo em escala geométrica"
    }),
    reqQuoteSpec: pick(lang, {
      en: "Request Quote for this Spec",
      zh: "以此规格获取报价",
      es: "Solicitar cotización para esta especificación",
      de: "Angebot für diese Spezifikation anfordern",
      fr: "Demander un devis pour cette spécification",
      pt: "Solicitar cotação para esta especificação"
    }),
    datasheetBtn: pick(lang, {
      en: "Datasheet",
      zh: "规格表",
      es: "Ficha técnica",
      de: "Datenblatt",
      fr: "Fiche technique",
      pt: "Ficha técnica"
    }),
    rollInputsTitle: pick(lang, {
      en: "Roll & Material Inputs",
      zh: "卷料与材质输入",
      es: "Parámetros de rollo y material",
      de: "Rollen- & Werkstoffeingaben",
      fr: "Paramètres de rouleau et matériau",
      pt: "Parâmetros de rolo e material"
    }),
    rollInputsDesc: pick(lang, {
      en: "Calculate total roll weight and shipping parameters based on alloy grade and dimensions.",
      zh: "根据合金牌号与卷面尺寸计算整卷重量及物流规划参数。",
      es: "Calcule el peso total del rollo y los parámetros de envío según la aleación y las dimensiones.",
      de: "Berechnen Sie das Gesamtrollengewicht und Versandparameter anhand von Legierung und Maßen.",
      fr: "Calculez le poids total du rouleau et les paramètres d'expédition selon l'alliage et les dimensions.",
      pt: "Calcule o peso total do rolo e os parâmetros de frete com base na liga e nas dimensões."
    }),
    alloyGradeLabel: pick(lang, {
      en: "Stainless Steel Alloy Grade",
      zh: "不锈钢合金牌号",
      es: "Calidad de aleación de acero inoxidable",
      de: "Edelstahllegierung",
      fr: "Nuance d'alliage d'acier inoxydable",
      pt: "Grau da liga de aço inoxidável"
    }),
    rollWidthLabel: pick(lang, {
      en: "Roll Width (m)",
      zh: "卷宽 (m)",
      es: "Ancho del rollo (m)",
      de: "Rollenbreite (m)",
      fr: "Largeur du rouleau (m)",
      pt: "Largura do rolo (m)"
    }),
    rollLengthLabel: pick(lang, {
      en: "Roll Length (m)",
      zh: "卷长 (m)",
      es: "Longitud del rollo (m)",
      de: "Rollenlänge (m)",
      fr: "Longueur du rouleau (m)",
      pt: "Comprimento do rolo (m)"
    }),
    massEstimatesTitle: pick(lang, {
      en: "Mass & Weight Estimates",
      zh: "质量与重量估算结果",
      es: "Estimaciones de masa y peso",
      de: "Masse- & Gewichtsschätzungen",
      fr: "Estimations de masse et poids",
      pt: "Estimativas de massa e peso"
    }),
    massEstimatesDesc: pick(lang, {
      en: "Calculated weight metrics for freight, storage, and handling planning.",
      zh: "用于物流运输、仓储与装卸规划的计算重量指标。",
      es: "Métricas calculadas para la planificación de transporte, almacenaje y manipulación.",
      de: "Berechnete Gewichtskennzahlen für Fracht-, Lager- und Handhabungsplanung.",
      fr: "Métriques calculées pour le transport, le stockage et la manutention.",
      pt: "Métricas calculadas para planejamento de frete, armazenagem e manuseio."
    }),
    unitAreaMass: pick(lang, {
      en: "Unit Area Mass",
      zh: "单位面积质量",
      es: "Masa por unidad de área",
      de: "Flächengewicht",
      fr: "Masse surfacique",
      pt: "Massa por unidade de área"
    }),
    totalAreaLabel: pick(lang, {
      en: "Total Area",
      zh: "总面积",
      es: "Área total",
      de: "Gesamtfläche",
      fr: "Surface totale",
      pt: "Área total"
    }),
    estRollMassTitle: pick(lang, {
      en: "Estimated Roll Mass",
      zh: "整卷预估总质量",
      es: "Masa estimada del rollo",
      de: "Geschätzte Rollenmasse",
      fr: "Masse estimée du rouleau",
      pt: "Massa estimada do rolo"
    }),
    crimpNotice: pick(lang, {
      en: "Includes standard 2% wire crimp allowance. Actual shipment weight may vary by ±3% due to weaving tension and tube core packaging.",
      zh: "已计入标准 2% 编织曲折延伸率。实际发运重量可能因编织张力与纸管芯包装存在 ±3% 的公差范围。",
      es: "Incluye una tolerancia estándar del 2% por ondulación del alambre. El peso real puede variar ±3% por tensión de tejido y núcleo.",
      de: "Enthält 2% Standard-Krümpfungszuschlag. Das tatsächliche Versandgewicht kann um ±3% durch Webspannung und Hülsenverpackung abweichen.",
      fr: "Comprend une tolérance standard de 2% pour l'ondulation du fil. Le poids réel d'expédition peut varier de ±3% selon la tension et le mandrin.",
      pt: "Inclui tolerância padrão de 2% para ondulação do arame. O peso real de envio pode variar ±3% devido à tensão de tecelagem e ao tubo interno."
    }),
    mtrNotice: pick(lang, {
      en: "Material Certificate 3.1 available when specified in the order",
      zh: "可按订单要求提供 EN 10204 3.1 材质证书",
      es: "Certificado de material 3.1 disponible cuando se especifique en el pedido",
      de: "Werkstoffprüfzeugnis 3.1 verfügbar, sofern im Auftrag spezifiziert",
      fr: "Certificat matière 3.1 disponible sur demande à la commande",
      pt: "Certificado de material 3.1 disponível quando especificado no pedido"
    }),
    slitWidthNotice: pick(lang, {
      en: "Custom roll slit widths available from 50mm to 2500mm",
      zh: "支持 50mm 至 2500mm 范围内的定制分条卷宽",
      es: "Anchos de corte personalizados disponibles de 50 mm a 2500 mm",
      de: "Kundenspezifische Rollenschlitzbreiten von 50 mm bis 2500 mm lieferbar",
      fr: "Largeurs de découpe sur mesure disponibles de 50 mm à 2500 mm",
      pt: "Larguras de corte sob medida disponíveis de 50 mm a 2500 mm"
    }),
    reqFreightQuote: pick(lang, {
      en: "Request Formal Freight & Price Quote",
      zh: "索取正式运费与产品报价",
      es: "Solicitar cotización formal de precio y flete",
      de: "Offizielles Fracht- & Preisangebot anfordern",
      fr: "Demander un devis formel prix et transport",
      pt: "Solicitar cotação formal de frete e preço"
    }),
    tableTitle: pick(lang, {
      en: "Standard ISO & ASTM Woven Wire Mesh Specifications",
      zh: "标准 ISO 与 ASTM 编织金属丝网规格表",
      es: "Especificaciones estándar ISO y ASTM de malla metálica tejida",
      de: "Standard ISO & ASTM Drahtgewebespezifikationen",
      fr: "Spécifications standard ISO et ASTM pour toiles métalliques",
      pt: "Especificações padrão ISO e ASTM para telas metálicas tecidas"
    }),
    tableDesc: pick(lang, {
      en: "Quick engineering lookup table for popular square woven stainless steel filter mesh count configurations.",
      zh: "常见方孔平纹编织不锈钢滤网目数配置的工程快速查阅表。",
      es: "Tabla de consulta rápida para configuraciones habituales de malla tejida cuadrada de acero inoxidable.",
      de: "Schnellübersicht für gängige Quadratmaschen-Edelstahlgewebekonfigurationen.",
      fr: "Tableau de référence rapide pour les configurations courantes de toiles carrées en acier inoxydable.",
      pt: "Tabela de consulta rápida para configurações comuns de telas tecidas quadradas de aço inoxidável."
    }),
    tableSearchPlaceholder: pick(lang, {
      en: "Filter mesh or micron...",
      zh: "按目数或微米过滤...",
      es: "Filtrar por malla o micras...",
      de: "Nach Maschen oder Mikron filtern...",
      fr: "Filtrer par maille ou micron...",
      pt: "Filtrar por malha ou mícrons..."
    }),
    noSpecsMatch: (q: string) => pick(lang, {
      en: `No specifications match your search query "${q}".`,
      zh: `未找到匹配 "${q}" 的规格。`,
      es: `No hay especificaciones que coincidan con la búsqueda "${q}".`,
      de: `Keine Spezifikationen entsprechen Ihrer Suche "${q}".`,
      fr: `Aucune spécification ne correspond à la recherche "${q}".`,
      pt: `Nenhuma especificação corresponde à busca "${q}".`
    }),
    tableFooterNotice: pick(lang, {
      en: "Values calculated for AISI 304/316 woven stainless steel wire mesh. Custom apertures woven upon request.",
      zh: "以上数值基于 AISI 304/316 编织不锈钢丝网核算。可按需定制特殊孔径编织规格。",
      es: "Valores calculados para malla tejida de acero inoxidable AISI 304/316. Aberturas especiales bajo pedido.",
      de: "Werte berechnet für AISI 304/316 Edelstahlgewebe. Sonderöffnungen auf Anfrage lieferbar.",
      fr: "Valeurs calculées pour toile tissée en acier inoxydable AISI 304/316. Ouvertures spéciales sur demande.",
      pt: "Valores calculados para malha tecida de aço inoxidável AISI 304/316. Aberturas especiais sob encomenda."
    }),
    viewAllCharts: pick(lang, {
      en: "View All Technical Charts",
      zh: "查看所有技术规格表",
      es: "Ver todas las tablas técnicas",
      de: "Alle technischen Tabellen anzeigen",
      fr: "Consulter tous les tableaux techniques",
      pt: "Ver todas as tabelas técnicas"
    })
  };
  const specColumns: DataTableColumn<StandardSpec>[] = useMemo(() => [{
    key: "mesh",
    header: pick(lang, {
      en: "Mesh Count",
      zh: "目数",
      es: "Número de mallas",
      de: "Maschenzahl",
      fr: "Nombre de mailles",
      pt: "Contagem de malha"
    }),
    sticky: true,
    mobileTitle: true,
    cell: row => <span className="font-mono font-semibold text-foreground">{row.mesh} Mesh</span>
  }, {
    key: "wireMm",
    header: pick(lang, {
      en: "Wire Dia (mm)",
      zh: "丝径 (mm)",
      es: "Diám. alambre (mm)",
      de: "Draht-Ø (mm)",
      fr: "Diam. fil (mm)",
      pt: "Diâm. arame (mm)"
    }),
    align: "right",
    cell: row => <span className="font-mono">{row.wireMm}</span>
  }, {
    key: "apertureMm",
    header: pick(lang, {
      en: "Aperture (mm)",
      zh: "孔径 (mm)",
      es: "Abertura (mm)",
      de: "Maschenweite (mm)",
      fr: "Ouverture (mm)",
      pt: "Abertura (mm)"
    }),
    align: "right",
    cell: row => <span className="font-mono">{row.apertureMm}</span>
  }, {
    key: "micron",
    header: pick(lang, {
      en: "Micron (µm)",
      zh: "微米 (µm)",
      es: "Micras (µm)",
      de: "Mikron (µm)",
      fr: "Microns (µm)",
      pt: "Mícrons (µm)"
    }),
    align: "right",
    emphasis: true,
    cell: row => <span className="font-mono text-primary font-medium">{row.micron} µm</span>
  }, {
    key: "openArea",
    header: pick(lang, {
      en: "Open Area (%)",
      zh: "开孔率 (%)",
      es: "Área abierta (%)",
      de: "Freie Siebfläche (%)",
      fr: "Surface ouverte (%)",
      pt: "Área aberta (%)"
    }),
    align: "right",
    cell: row => <span className="font-mono">{row.openArea}%</span>
  }, {
    key: "weightKgM2",
    header: pick(lang, {
      en: "Mass (kg/m²)",
      zh: "质量 (kg/m²)",
      es: "Masa (kg/m²)",
      de: "Masse (kg/m²)",
      fr: "Masse (kg/m²)",
      pt: "Massa (kg/m²)"
    }),
    align: "right",
    cell: row => <span className="font-mono">{row.weightKgM2}</span>
  }, {
    key: "standard",
    header: pick(lang, {
      en: "Standard",
      zh: "标准",
      es: "Norma",
      de: "Norm",
      fr: "Norme",
      pt: "Norma"
    }),
    cell: row => <Badge variant="outline" className="font-mono text-sm">
          {row.standard}
        </Badge>
  }], [lang]);
  return <section id="wovencalculations" data-nav="light" className="bg-muted/50 text-foreground mf-section">
      <div className="mf-container">
        {/* Section Header */}
        <Reveal>
          <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
            <Badge variant="outline" className="mb-3 px-3 py-1 font-mono text-sm tracking-wider uppercase border-primary/30 text-primary">
              {copy.badge}
            </Badge>
            <h2 className="mf-h2 font-default text-foreground mb-4">
              {copy.h2}
            </h2>
            <p className="text-muted-foreground mf-lead max-w-[65ch] mx-auto">
              {copy.lead}
            </p>
          </div>
        </Reveal>

        {/* Tabbed Interactive Calculators */}
        <Tabs defaultValue="aperture" className="w-full max-w-5xl mx-auto">
          <div className="flex justify-center mb-8">
            <TabsList className="bg-muted p-1 rounded-lg border border-border">
              <TabsTrigger value="aperture" className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg">
                <Grid2x2 className="size-4" />
                <span>{copy.tabAperture}</span>
              </TabsTrigger>
              <TabsTrigger value="weight" className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg">
                <Scale className="size-4" />
                <span>{copy.tabWeight}</span>
              </TabsTrigger>
              <TabsTrigger value="reference" className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg">
                <FileText className="size-4" />
                <span>{copy.tabReference}</span>
              </TabsTrigger>
            </TabsList>
          </div>

          {/* TAB 1: APERTURE & OPEN AREA */}
          <TabsContent value="aperture" className="focus-visible:outline-none">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              {/* Input Card */}
              <Card className="lg:col-span-6 bg-card border-border">
                <CardHeader className="border-b border-border/60 pb-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xl font-semibold flex items-center gap-2 text-card-foreground">
                      <SlidersHorizontal className="size-5 text-primary" />
                      {copy.meshInputsTitle}
                    </CardTitle>
                    <Button variant="ghost" size="sm" onClick={resetApertureCalc} className="text-muted-foreground hover:text-foreground text-sm">
                      <RotateCcw className="size-3.5 mr-1" /> {copy.resetBtn}
                    </Button>
                  </div>
                  <CardDescription>
                    {copy.meshInputsDesc}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6 space-y-6">
                  {/* Mesh Count Input */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="ap-mesh" className="text-sm font-medium text-foreground">
                        {copy.labelMeshCount}
                      </Label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex items-center text-sm text-muted-foreground cursor-help">
                              <CircleHelp className="size-3.5 mr-1 text-muted-foreground/70" />
                              {copy.warpWeftInch}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-xs text-sm">
                              {copy.meshTooltip}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <div className="relative">
                      <Input id="ap-mesh" type="number" min="1" max="635" step="1" value={apMesh || ""} onChange={e => setApMesh(Math.max(1, parseFloat(e.target.value) || 0))} className="font-mono text-base pr-20" />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-mono text-muted-foreground pointer-events-none">
                        holes/in
                      </span>
                    </div>
                  </div>

                  {/* Wire Diameter Input */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="ap-wire" className="text-sm font-medium text-foreground">
                        {copy.labelWireDia}
                      </Label>
                      <span className="text-sm font-mono text-muted-foreground">
                        {(apWireMm / 25.4).toFixed(5)} inch
                      </span>
                    </div>
                    <div className="relative">
                      <Input id="ap-wire" type="number" min="0.01" max="5" step="0.005" value={apWireMm || ""} onChange={e => setApWireMm(Math.max(0.001, parseFloat(e.target.value) || 0))} className="font-mono text-base pr-12" />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-mono text-muted-foreground pointer-events-none">
                        mm
                      </span>
                    </div>
                  </div>

                  {/* Weave Type Select */}
                  <div className="space-y-2">
                    <Label htmlFor="weave-type" className="text-sm font-medium text-foreground">
                      {copy.weaveStyleLabel}
                    </Label>
                    <Select value={weaveType} onValueChange={setWeaveType}>
                      <SelectTrigger id="weave-type" className="w-full">
                        <SelectValue placeholder="Select weave type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="plain">{copy.stylePlain}</SelectItem>
                        <SelectItem value="twilled">{copy.styleTwilled}</SelectItem>
                        <SelectItem value="dutch">{copy.styleDutch}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Formula Callout */}
                  <div className="p-3 bg-muted/60 rounded-lg border border-border text-sm space-y-1 font-mono text-muted-foreground">
                    <div className="flex items-center gap-1 font-medium text-foreground">
                      <Info className="size-3.5 text-primary" /> {copy.keyFormulasTitle}
                    </div>
                    <div>Pitch (p) = 25.4 / Mesh</div>
                    <div>Aperture (w) = Pitch - Wire Diameter</div>
                    <div>Open Area (%) = (w / p)² × 100</div>
                  </div>
                </CardContent>
              </Card>

              {/* Live Output Card */}
              <Card className="lg:col-span-6 bg-card border-border">
                <CardHeader className="border-b border-border/60 pb-4">
                  <CardTitle className="text-xl font-semibold flex items-center justify-between text-card-foreground">
                    <span>{copy.calcSpecTitle}</span>
                    {rawApertureMm > 0 ? <Badge variant="default" className="bg-primary text-primary-foreground font-mono text-sm">
                        {copy.validGeometry}
                      </Badge> : <Badge variant="destructive" className="font-mono text-sm">
                        {copy.invalidWire}
                      </Badge>}
                  </CardTitle>
                  <CardDescription>
                    {copy.calcSpecDesc}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6 space-y-6">
                  {rawApertureMm <= 0 ? <div className="p-6 bg-destructive/10 text-destructive rounded-lg border border-destructive/20 text-sm leading-relaxed">
                      {copy.interferenceWarning(apWireMm, pitchMm.toFixed(4), apMesh)}
                    </div> : <>
                      {/* Metric Grid */}
                      <div aria-live="polite" className="grid grid-cols-2 gap-4">
                        <div className="p-4 rounded-lg bg-muted/40 border border-border space-y-1">
                          <span className="text-sm text-muted-foreground font-medium uppercase tracking-wider">
                            {copy.apertureSizeLabel}
                          </span>
                          <div className="text-2xl sm:text-3xl font-bold font-mono text-foreground">
                            {apertureMm.toFixed(3)}{" "}
                            <span className="text-sm font-normal text-muted-foreground">mm</span>
                          </div>
                          <div className="mf-caption font-mono text-primary font-medium">
                            {apertureMicron.toFixed(1)} µm
                          </div>
                        </div>

                        <div className="p-4 rounded-lg bg-muted/40 border border-border space-y-1">
                          <span className="text-sm text-muted-foreground font-medium uppercase tracking-wider">
                            {copy.openAreaLabel}
                          </span>
                          <div className="text-2xl sm:text-3xl font-bold font-mono text-foreground">
                            {openAreaPercent.toFixed(1)}
                            <span className="text-sm font-normal text-muted-foreground">%</span>
                          </div>
                          <div className="mf-caption font-mono text-muted-foreground">
                            {copy.passageRatio}
                          </div>
                        </div>

                        <div className="p-4 rounded-lg bg-muted/40 border border-border space-y-1">
                          <span className="text-sm text-muted-foreground font-medium uppercase tracking-wider">
                            {copy.pitchLabel}
                          </span>
                          <div className="text-xl font-bold font-mono text-foreground">
                            {pitchMm.toFixed(4)}{" "}
                            <span className="text-sm font-normal text-muted-foreground">mm</span>
                          </div>
                          <div className="mf-caption font-mono text-muted-foreground">
                            {copy.centerToCenter}
                          </div>
                        </div>

                        <div className="p-4 rounded-lg bg-muted/40 border border-border space-y-1">
                          <span className="text-sm text-muted-foreground font-medium uppercase tracking-wider">
                            {copy.ratioLabel}
                          </span>
                          <div className="text-xl font-bold font-mono text-foreground">
                            {(apertureMm / apWireMm).toFixed(2)}
                          </div>
                          <div className="mf-caption font-mono text-muted-foreground">
                            {copy.structuralProp}
                          </div>
                        </div>
                      </div>

                      {/* Visual Wire Grid Diagram Illustration */}
                      <div className="p-5 rounded-lg bg-muted/30 border border-border space-y-3">
                        <div className="flex items-center justify-between text-sm font-medium text-foreground">
                          <span>{copy.scaleModelTitle}</span>
                          <span className="text-muted-foreground font-mono">
                            Wire: {apWireMm}mm | Open: {apertureMm.toFixed(3)}mm
                          </span>
                        </div>

                        <div className="w-full h-32 rounded-lg bg-card border border-border p-3 flex items-center justify-center relative overflow-hidden">
                          <div className="w-24 h-24 relative flex items-center justify-center border-2 border-primary/40 rounded bg-primary/5">
                            <div className="border border-dashed border-primary bg-primary/20 flex items-center justify-center transition-all duration-300" style={{
                          width: `${Math.min(100, Math.max(15, apertureMm / pitchMm * 100))}%`,
                          height: `${Math.min(100, Math.max(15, apertureMm / pitchMm * 100))}%`
                        }}>
                              <span className="mf-caption font-mono font-bold text-primary">
                                {apertureMicron < 1000 ? `${Math.round(apertureMicron)}µm` : `${apertureMm.toFixed(2)}mm`}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* CTA inside Card */}
                      <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-3">
                        <Link to="/RequestAQuotePage" className="inline-flex items-center text-sm font-semibold text-primary hover:underline group">
                          <span>{copy.reqQuoteSpec}</span>
                          <ArrowRight className="size-4 ml-1.5" />
                        </Link>
                        <Button asChild variant="outline" size="sm" className="w-full sm:w-auto">
                          <Link to="/DatasheetsPage">
                            <Download className="size-4 mr-1.5" /> {copy.datasheetBtn}
                          </Link>
                        </Button>
                      </div>
                    </>}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* TAB 2: WEIGHT & ROLL ESTIMATOR */}
          <TabsContent value="weight" className="focus-visible:outline-none">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              {/* Weight Inputs */}
              <Card className="lg:col-span-6 bg-card border-border">
                <CardHeader className="border-b border-border/60 pb-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xl font-semibold flex items-center gap-2 text-card-foreground">
                      <Scale className="size-5 text-primary" />
                      {copy.rollInputsTitle}
                    </CardTitle>
                    <Button variant="ghost" size="sm" onClick={resetWeightCalc} className="text-muted-foreground hover:text-foreground text-sm">
                      <RotateCcw className="size-3.5 mr-1" /> {copy.resetBtn}
                    </Button>
                  </div>
                  <CardDescription>
                    {copy.rollInputsDesc}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6 space-y-6">
                  {/* Alloy Grade */}
                  <div className="space-y-2">
                    <Label htmlFor="alloy-select" className="text-sm font-medium text-foreground">
                      {copy.alloyGradeLabel}
                    </Label>
                    <Select value={alloy} onValueChange={setAlloy}>
                      <SelectTrigger id="alloy-select" className="w-full">
                        <SelectValue placeholder="Select alloy grade" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(DENSITY_MAP).map(([key, val]) => <SelectItem key={key} value={key}>
                            {val.name} ({val.density} kg/m³)
                          </SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {/* Mesh Count */}
                    <div className="space-y-2">
                      <Label htmlFor="wt-mesh" className="text-sm font-medium text-foreground">
                        {copy.labelMeshCount}
                      </Label>
                      <Input id="wt-mesh" type="number" min="1" max="600" value={wtMesh || ""} onChange={e => setWtMesh(Math.max(1, parseFloat(e.target.value) || 0))} className="font-mono" />
                    </div>

                    {/* Wire Diameter */}
                    <div className="space-y-2">
                      <Label htmlFor="wt-wire" className="text-sm font-medium text-foreground">
                        {copy.labelWireDia}
                      </Label>
                      <Input id="wt-wire" type="number" min="0.01" max="5" step="0.01" value={wtWireMm || ""} onChange={e => setWtWireMm(Math.max(0.001, parseFloat(e.target.value) || 0))} className="font-mono" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {/* Roll Width */}
                    <div className="space-y-2">
                      <Label htmlFor="roll-width" className="text-sm font-medium text-foreground">
                        {copy.rollWidthLabel}
                      </Label>
                      <Input id="roll-width" type="number" min="0.1" max="3.0" step="0.05" value={rollWidth || ""} onChange={e => setRollWidth(Math.max(0.1, parseFloat(e.target.value) || 0))} className="font-mono" />
                    </div>

                    {/* Roll Length */}
                    <div className="space-y-2">
                      <Label htmlFor="roll-length" className="text-sm font-medium text-foreground">
                        {copy.rollLengthLabel}
                      </Label>
                      <Input id="roll-length" type="number" min="0.5" max="500" step="1" value={rollLength || ""} onChange={e => setRollLength(Math.max(0.1, parseFloat(e.target.value) || 0))} className="font-mono" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Weight Results Card */}
              <Card className="lg:col-span-6 bg-card border-border">
                <CardHeader className="border-b border-border/60 pb-4">
                  <CardTitle className="text-xl font-semibold text-card-foreground">
                    {copy.massEstimatesTitle}
                  </CardTitle>
                  <CardDescription>
                    {copy.massEstimatesDesc}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6 space-y-6">
                  <div aria-live="polite" className="grid grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg bg-muted/40 border border-border space-y-1">
                      <span className="text-sm text-muted-foreground font-medium uppercase tracking-wider">
                        {copy.unitAreaMass}
                      </span>
                      <div className="text-2xl sm:text-3xl font-bold font-mono text-foreground">
                        {weightKgPerM2.toFixed(3)}{" "}
                        <span className="text-sm font-normal text-muted-foreground">kg/m²</span>
                      </div>
                      <div className="mf-caption font-mono text-muted-foreground">
                        {(weightKgPerM2 * 0.204816).toFixed(3)} lbs/ft²
                      </div>
                    </div>

                    <div className="p-4 rounded-lg bg-muted/40 border border-border space-y-1">
                      <span className="text-sm text-muted-foreground font-medium uppercase tracking-wider">
                        {copy.totalAreaLabel}
                      </span>
                      <div className="text-2xl sm:text-3xl font-bold font-mono text-foreground">
                        {totalAreaM2.toFixed(2)}{" "}
                        <span className="text-sm font-normal text-muted-foreground">m²</span>
                      </div>
                      <div className="mf-caption font-mono text-muted-foreground">
                        {(totalAreaM2 * 10.7639).toFixed(1)} sq ft
                      </div>
                    </div>
                  </div>

                  {/* Total Shipment Weight Banner */}
                  <div aria-live="polite" className="p-5 rounded-lg bg-primary/10 border border-primary/20 space-y-2">
                    <div className="flex items-center justify-between text-sm font-semibold uppercase tracking-wider text-primary">
                      <span>{copy.estRollMassTitle}</span>
                      <span>Alloy: {alloy}</span>
                    </div>
                    <div className="flex items-baseline justify-between">
                      <span className="text-3xl sm:text-4xl font-extrabold font-mono text-foreground">
                        {totalRollWeightKg.toFixed(2)}{" "}
                        <span className="text-lg font-normal text-muted-foreground">kg</span>
                      </span>
                      <span className="text-xl font-mono text-primary font-bold">
                        {totalRollWeightLbs.toFixed(1)} lbs
                      </span>
                    </div>
                    <p className="mf-caption text-muted-foreground leading-normal">
                      {copy.crimpNotice}
                    </p>
                  </div>

                  {/* Feature Checks */}
                  <div className="space-y-2 mf-caption text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <CircleCheck className="size-4 text-primary shrink-0" />
                      <span>{copy.mtrNotice}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CircleCheck className="size-4 text-primary shrink-0" />
                      <span>{copy.slitWidthNotice}</span>
                    </div>
                  </div>

                  <div className="pt-2">
                    <Link to="/RequestAQuotePage" className="inline-flex items-center text-sm font-semibold text-primary hover:underline group">
                      <span>{copy.reqFreightQuote}</span>
                      <ArrowRight className="size-4 ml-1.5" />
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* TAB 3: REFERENCE TABLE */}
          <TabsContent value="reference" className="focus-visible:outline-none">
            <Card className="bg-card border-border">
              <CardHeader className="border-b border-border/60 pb-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <CardTitle className="text-xl font-semibold text-card-foreground">
                      {copy.tableTitle}
                    </CardTitle>
                    <CardDescription className="mf-caption text-muted-foreground">
                      {copy.tableDesc}
                    </CardDescription>
                  </div>
                  <div className="w-full sm:w-64">
                    <Input type="text" placeholder={copy.tableSearchPlaceholder} value={tableSearch} onChange={e => setTableSearch(e.target.value)} className="text-sm" />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-4 p-4 sm:p-6">
                <DataTable columns={specColumns} rows={filteredSpecs} getRowKey={row => row.mesh.toString()} minWidth={680} empty={copy.noSpecsMatch(tableSearch)} />

                <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-border/60 mf-caption text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Info className="size-4 text-primary shrink-0" />
                    <span>{copy.tableFooterNotice}</span>
                  </div>
                  <Button asChild variant="outline" size="sm">
                    <Link to="/TechnicalResourcesPage">
                      {copy.viewAllCharts}
                      <ArrowRight className="size-3.5 ml-1" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </section>;
}